// ---------------------------------------------------------------------------
// Bezüge: eine Textstelle gehört zu einem oder mehreren Elementen (sie sprechen, handeln, denken oder werden beschrieben) – anders als eine Nennung („Clara“
// meint Clara) nicht an ein Wort gebunden. Gespeichert als `[Textstelle]{@clara @paul}`, optional mit Art: `[„Komm!“]{@clara rede}`.
// Die Stelle darf Nennungen enthalten (`[„Komm!“, sagt Clara[@clara].]{@clara rede}`). Bezüge stehen nur in Textdokumenten; sie lernen
// keine Vorkommen und werden nie vorgeschlagen.
// ---------------------------------------------------------------------------

import { closingBracket } from './brackets.js';
import { stripTimeMarks } from './timeMarks.js';

/**
 * Art eines Bezugs: Rede, Handlung, Gedanke, Beschreibung – und Wissen: erfährt (eine Figur erfährt etwas), enthüllt (der Leser erfährt
 * es; knowledge.ts); fehlend = ohne Angabe (ältere Bezüge – neue bekommen immer eine Art)
 */
export const SPAN_ROLES = ['rede', 'handlung', 'gedanke', 'beschreibung', 'erfährt', 'enthüllt'] as const;
export type SpanRole = (typeof SPAN_ROLES)[number];

const KEY = '[A-Za-z][A-Za-z0-9_]{0,29}';
/** Was auf `]` folgt: die Schlüssel, optional die Art */
const SUFFIX = new RegExp(`^\\{(@${KEY}(?:[ \\t]+@${KEY})*)(?:[ \\t]+(${SPAN_ROLES.join('|')}))?\\}`);

export interface SpanRef {
  /** `[` … bis hinter `}` */
  from: number;
  to: number;
  /** die Textstelle selbst (zwischen den Klammern) */
  textFrom: number;
  textTo: number;
  keys: string[];
  role: SpanRole | null;
}

/** Der Bezug, der genau bei `at` (mit „[“) beginnt, sonst null. */
export function spanAt(text: string, at: number): SpanRef | null {
  if (text[at] !== '[' || (at > 0 && text[at - 1] === '\\')) return null;
  const close = closingBracket(text, at);
  if (close < 0) return null;
  const m = SUFFIX.exec(text.slice(close + 1, close + 1 + 400));
  if (!m) return null;
  const keys = [...new Set(m[1]!.split(/[ \t]+/).map((k) => k.slice(1).toLowerCase()))];
  return { from: at, to: close + 1 + m[0].length, textFrom: at + 1, textTo: close, keys, role: (m[2] as SpanRole | undefined) ?? null };
}

/** Alle Bezüge eines Textes (Markdown), in Textreihenfolge. */
export function spansIn(text: string): SpanRef[] {
  const out: SpanRef[] = [];
  if (!text.includes(']{@')) return out;
  for (let i = text.indexOf('['); i >= 0; i = text.indexOf('[', i + 1)) {
    const s = spanAt(text, i);
    if (!s) continue;
    out.push(s);
    i = s.to - 1;
  }
  return out;
}

/**
 * Text ohne Bezüge und Zeitmarken: jede Textstelle bleibt, die Klammern, Schlüssel und Zeitangaben fallen weg (Nennungen darin
 * bleiben). Grundlage für Klartext, Wortzählung und mögliche Verweise.
 */
export function stripSpans(text: string): string {
  const spans = spansIn(text);
  if (spans.length === 0) return stripTimeMarks(text);
  let out = '';
  let last = 0;
  for (const s of spans) {
    out += text.slice(last, s.from) + text.slice(s.textFrom, s.textTo);
    last = s.to;
  }
  return stripTimeMarks(out + text.slice(last));
}

/** Endung eines Bezugs im Markdown: `{@clara @paul rede}` */
export const spanSuffix = (keys: readonly string[], role: SpanRole | null): string => `{${keys.map((k) => `@${k}`).join(' ')}${role ? ` ${role}` : ''}}`;

/** Art, die sich aus der Stelle ergibt: in Anführungszeichen ist sie Rede, sonst ohne Angabe. */
export const spanRoleOf = (text: string): SpanRole | null => (/^[„“”"«»‚‘’'›‹]/.test(text.trim()) ? 'rede' : null);

const PASSAGE = new RegExp(`\\[([^[\\]\\n]+)\\]\\(@(${KEY})\\)`, 'g');
const WORDS = /[\p{L}\p{N}_-]+/gu;

/**
 * Ältere Texte: verknüpfte Passagen mit mehr als vier Wörtern („[Clara steigt auf den Turm](@clara)“) waren gemeint als Bezug, nicht
 * als Name – sie werden Bezüge (in Anführungszeichen: Rede). Kürzere bleiben Nennungen.
 */
export const passagesToSpans = (text: string): string =>
  text.replace(PASSAGE, (all, passage: string, key: string) => {
    const plain = passage.replace(/\*\*|~~|[*`]/g, '');
    return (plain.match(WORDS)?.length ?? 0) > 4 ? `[${passage}]${spanSuffix([key.toLowerCase()], spanRoleOf(plain))}` : all;
  });
